import classNames from 'classnames';
import AbbreviationsListLayout from 'modules/Abbreviations/components/AbbreviationsListLayout';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArtboardLayout from 'components/ArtboardLayout';
import carouselItemStyles from 'components/Carousel/styles.module.scss';
import ReferenceCitationElement from 'components/ReferenceCitationElement';
import Spacer from 'components/Spacer';
import { LayoutType } from 'const';
import { setFrozenEntityIdAction } from 'containers/FrozenAction/actions';
import { getFrozenEntityId } from 'containers/FrozenAction/selectors';
import { ColumnRelationMap, CombinedDocumentsMap, LayeredRelationsMap, LayoutMap, MasterScreen, SSIMap } from 'models';
import { isEmptyLayout } from 'utils/layouts/isLayoutEmpty';
import { isLayoutScrollable } from 'utils/layouts/isLayoutScrollable';
import { useArtboardLayoutContainerDragSource } from './dnd';
import styles from './styles.module.scss';
import { useShouldDisableDragAndDeleteActions } from './utils';

type Props = {
  disableDragAndDelete?: boolean;
  documents?: CombinedDocumentsMap;
  groupLayoutId?: string; // should be mandatory
  isFirst?: boolean; // should be mandatory
  isLastLayout?: boolean;
  isSectionActive: boolean;
  isReadOnly?: boolean;
  layout: LayoutMap;
  layoutId: string;
  layoutRelations?: LayeredRelationsMap;
  moveableArtboardLayoutId?: string;
  position: number;
  saveLayoutRef: (layoutId: string, element: HTMLDivElement) => void;
  sectionDisplayName: string;
  sectionName: string;
  sectionStyles: MasterScreen.SectionStylesMap;
  ssi: SSIMap | null;
  dataTestId: string;
};

const ArtboardLayoutContainer: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const frozenEntityId = useSelector(getFrozenEntityId);
  const {
    moveableArtboardLayoutId,
    saveLayoutRef,
    layout,
    documents,
    groupLayoutId,
    isFirst,
    isSectionActive,
    layoutId,
    sectionDisplayName,
    sectionName,
    sectionStyles,
    ssi,
    dataTestId = 'layoutContainer',
    position,
    layoutRelations,
    isReadOnly,
    disableDragAndDelete,
    isLastLayout,
  } = props;
  const layoutType = layout.get('type');
  const [isDraggable, setIsDraggable] = useState(false);
  const prevMoveableIdRef = useRef<string | undefined>();
  const prevIsDraggableRef = useRef<boolean>(false);
  const artboardLayoutContainerRef = useRef<HTMLDivElement | undefined>(undefined);

  useEffect(() => {
    const next = moveableArtboardLayoutId === layoutId;
    prevMoveableIdRef.current = moveableArtboardLayoutId;
    prevIsDraggableRef.current = next;
    setIsDraggable(next);
  }, [moveableArtboardLayoutId, layoutId]);

  const handleSaveLayoutRef = useCallback((element: HTMLDivElement) => {
    saveLayoutRef(layoutId, element);
    artboardLayoutContainerRef.current = element;
  }, [saveLayoutRef, layoutId]);

  const disableDragAndDeleteActions = useShouldDisableDragAndDeleteActions({
    groupLayoutId,
    disableDragAndDelete,
    documents,
    layout,
    layoutRelations,
    isLastLayout,
  });

  const { connectDragSource } = useArtboardLayoutContainerDragSource({ groupLayoutId, layout, documents, canDrag: isDraggable });
  const layoutDataTestId = `${dataTestId}-layout0`; // we have only 1 layout per container now

  const renderArtboardLayout = (): React.ReactNode => {
    switch (layoutType) {
      case LayoutType.SPACER:
        return (
          <Spacer
            connectDragSource={connectDragSource}
            layout={layout}
            notEditable={!!ssi}
            dataTestId={layoutDataTestId}
          />
        );
      case LayoutType.REFERENCE_CITATION_ELEMENT:
        return (
          <ReferenceCitationElement
            artboardLayoutContainer={artboardLayoutContainerRef.current}
            connectDragSource={connectDragSource}
            layout={layout}
            layoutId={layoutId}
            notEditable={!!ssi}
            sectionName={sectionName}
            ssi={ssi}
            dataTestId={layoutDataTestId}
          />
        );
      case LayoutType.ABBREVIATIONS_LIST:
        return (
          <AbbreviationsListLayout
            artboardLayoutContainer={artboardLayoutContainerRef.current}
            connectDragSource={connectDragSource}
            layout={layout}
            dataTestId={layoutDataTestId}
          />
        );
      default:
        return (
          <ArtboardLayout
            artboardLayoutContainer={artboardLayoutContainerRef.current}
            connectDragSource={connectDragSource}
            disableDragAndDelete={disableDragAndDeleteActions}
            groupLayoutId={groupLayoutId ?? ''}
            isFirst={!!isFirst}
            isSectionActive={isSectionActive}
            layout={layout}
            layoutId={layoutId}
            sectionDisplayName={sectionDisplayName}
            sectionName={sectionName}
            sectionStyles={sectionStyles}
            ssi={ssi}
            dataTestId={layoutDataTestId}
          />
        );
    }
  };

  const isScrollable = isLayoutScrollable(layout);
  let isStretchingLayout = false;
  if (layoutRelations && documents) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const empty = isEmptyLayout(layout, layoutRelations, documents as any);
    if (
      empty &&
      !isScrollable &&
      !((layoutRelations.get(layout.get('relationId')) as ColumnRelationMap).getIn(['styles', 'rowsHeight']).every(Boolean)) &&
      layoutType !== LayoutType.ABBREVIATIONS_LIST
    ) {
      isStretchingLayout = true;
    }
  }
  const handleClickOutsideFrozenAction = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!frozenEntityId) {
      return;
    }
    const target = event.target as HTMLElement;
    const slideAction = target.closest(`.${carouselItemStyles.carouselItem}`);
    if (slideAction?.id === frozenEntityId) {

      return;
    }
    if (frozenEntityId === layout.get('id')) {
      return;
    }
    dispatch(setFrozenEntityIdAction(null));
  };

  return (
    <div
      onClick={handleClickOutsideFrozenAction }
      ref={handleSaveLayoutRef}
      className={classNames(styles.ArtboardLayoutContainer, {
        [styles.spacerContainer]: layoutType === LayoutType.SPACER,
        [styles.insideOfSSI]: !!ssi && !groupLayoutId,
        [styles.stretchingLayout]: isStretchingLayout,
        [styles.scrollable]: isScrollable,
        [styles.disableDrop]: isReadOnly,
      })}
      style={{ order: position }}
      data-testid={dataTestId}
    >
      {renderArtboardLayout()}
    </div>
  );
};

export default ArtboardLayoutContainer;
